/**
 * Boomfection Landing — Game UI Module
 * Start button, lobby players, slot edit, stats panel, game UI, timers
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 *
 * SECURITY NOTE: innerHTML usage below is safe — all user-provided strings are
 * sanitized via escapeHtml() from /js/ui/utils.js, numeric values are coerced,
 * and color hex values come from trusted game state (PLAYER_COLORS constants).
 * This is an existing pattern moved verbatim from the monolithic landing.js
 * as a pure structural refactor with no behavioral changes.
 */

import { escapeHtml } from '/js/ui/utils.js';
import { PLAYER_COLORS } from '/js/game/GameCore.js';
import { t } from '/js/i18n.js';

/** Difficulty labels — single source of truth for AI difficulty display */
export const DIFFICULTY_LABELS = {
    easy: '\u{1F60A} Anf\u00E4nger',
    advanced: '\u{1F9E0} Fortgeschritten',
    normal: '\u{1F642} Casual',
    medium: '\u{1F610} Stratege',
    hard: '\u{1F608} Profi',
    insane: '\u{1F480} Meister'
};

/** Returns translated difficulty labels (i18n-aware, falls back to DIFFICULTY_LABELS) */
function getDifficultyLabel(key) {
    const translated = t(`ai.${key}`);
    return translated !== `ai.${key}` ? translated : DIFFICULTY_LABELS[key] || DIFFICULTY_LABELS.normal;
}

export const SLOT_TYPES = { PLAYER: 'player', AI: 'ai', BLOCKED: 'blocked' };

// Timer state
let gameStartTime = null;
let gameTimeInterval = null;
let turnStartTime = null;
let turnTimeInterval = null;

/**
 * Updates the start button visibility and waiting text
 * @param {Object} client - MultiplayerClient instance
 */
export function updateStartButton(client) {
    const btn = document.getElementById('btn-start-game');
    const state = client.getGameState();
    if (!state) return;

    const isHost = client.isHost();
    const canStartResult = state.canStart || { canStart: false };
    const canStart = canStartResult.canStart;

    if (isHost && canStart) {
        btn.classList.remove('hidden');
        btn.disabled = false;
    } else if (isHost) {
        btn.classList.remove('hidden');
        btn.disabled = true;
    } else {
        btn.classList.add('hidden');
    }

    const waitingText = document.querySelector('.waiting-text');
    if (waitingText) {
        if (!canStart && canStartResult.emptyPlayerSlots) {
            waitingText.textContent = t('lobby.waitingForPlayers', { n: canStartResult.emptyPlayerSlots });
            waitingText.classList.remove('hidden');
        } else if (!canStart && canStartResult.reason === 'not_enough_participants') {
            waitingText.textContent = t('lobby.needMoreParticipants');
            waitingText.classList.remove('hidden');
        } else if (!isHost && canStart) {
            waitingText.textContent = t('lobby.waitingForHost');
            waitingText.classList.remove('hidden');
        } else {
            waitingText.classList.add('hidden');
        }
    }
}

/**
 * Updates the lobby player/slot list
 * @param {Object} client - MultiplayerClient instance
 * @param {Function} showSlotEditModalFn - callback for slot edit
 */
export function updateLobbyPlayers(client, showSlotEditModalFn) {
    const state = client.getGameState();
    if (!state) return;
    const isHost = client.isHost();
    const slots = state.slots || [];
    const players = state.players || [];
    const container = document.getElementById('lobby-slots');

    let html = '';

    for (const slot of slots) {
        const player = players.find((p) => p.id === slot.playerId);
        const slotType = slot.type;
        const editBtn =
            isHost && slot.index > 0
                ? `<button class="btn-slot-edit" data-slot="${slot.index}" title="${t('lobby.editSlot')}">\u270E</button>`
                : '';

        if (slotType === SLOT_TYPES.BLOCKED) {
            html += `<div class="slot-item blocked" data-slot-index="${slot.index}"><div class="slot-status-icon">\u2B1B</div><span class="slot-label">${t('lobby.blocked')}</span>${editBtn}</div>`;
        } else if (slotType === SLOT_TYPES.AI) {
            const diffLabel = getDifficultyLabel(slot.aiDifficulty || 'normal');
            const colorHex = slot.colorId ? PLAYER_COLORS.find((c) => c.id === slot.colorId)?.hex || '#888' : '#555';
            html += `<div class="slot-item ai" data-slot-index="${slot.index}"><div class="slot-color" style="background: ${colorHex}"></div><span class="slot-label">${t('lobby.aiLabel')} ${diffLabel}</span><span class="slot-status ready">\u2713</span>${editBtn}</div>`;
        } else if (player) {
            html += `<div class="slot-item player filled ${player.isConnected === false ? 'disconnected' : ''}" data-slot-index="${slot.index}"><div class="slot-color" style="background: ${player.color?.hex || '#888'}"></div><span class="slot-label">${escapeHtml(player.name)}</span>${
                player.id === client.game?.hostId ? `<span class="slot-badge">${t('lobby.hostBadge')}</span>` : ''
            }${
                player.isAI ? `<span class="slot-badge ai">${t('lobby.aiBadge')}</span>` : ''
            }<span class="slot-status ready">\u2713</span></div>`;
        } else {
            html += `<div class="slot-item player empty" data-slot-index="${slot.index}"><div class="slot-color empty-color"></div><span class="slot-label text-muted">${t('lobby.waitingForPlayer')}</span><span class="slot-status waiting">\u25CB</span>${editBtn}</div>`;
        }
    }

    container.innerHTML = html;
    container.classList.remove('compact-slots', 'extra-compact-slots');
    if (slots.length >= 9) container.classList.add('compact-slots', 'extra-compact-slots');
    else if (slots.length >= 7) container.classList.add('compact-slots');

    if (isHost) {
        container.querySelectorAll('.btn-slot-edit').forEach((btn) => {
            btn.onclick = (e) => {
                e.stopPropagation();
                showSlotEditModalFn(parseInt(btn.dataset.slot));
            };
        });
    }
    updateStartButton(client);
}

/**
 * Shows the slot edit modal for host configuration
 * @param {number} slotIndex
 * @param {Object} client - MultiplayerClient instance
 */
export function showSlotEditModal(slotIndex, client) {
    const state = client.getGameState();
    const slot = state.slots.find((s) => s.index === slotIndex);
    if (!slot) return;

    const colorOptions = PLAYER_COLORS.map(
        (c) => `<option value="${c.id}"${slot.colorId === c.id ? ' selected' : ''}>${c.name}</option>`
    ).join('');

    const modalHtml =
        `<div id="slot-edit-modal" class="modal-overlay"><div class="modal-content animate-scale-in">` +
        `<h3>${t('lobby.editSlotTitle', { n: slotIndex + 1 })}</h3>` +
        `<div class="form-group"><label>${t('lobby.slotType')}</label><select class="form-control" id="slot-edit-type">` +
        `<option value="player"${slot.type === 'player' ? ' selected' : ''}>${t('lobby.typePlayer')}</option>` +
        `<option value="ai"${slot.type === 'ai' ? ' selected' : ''}>${t('lobby.typeAI')}</option>` +
        `<option value="blocked"${slot.type === 'blocked' ? ' selected' : ''}>${t('lobby.typeBlocked')}</option></select></div>` +
        `<div class="form-group ai-options ${slot.type === 'ai' ? '' : 'hidden'}"><label>${t('lobby.aiColor')}</label><select class="form-control" id="slot-edit-color"><option value="">${t('lobby.aiColorAuto')}</option>${colorOptions}</select></div>` +
        `<div class="form-group ai-options ${slot.type === 'ai' ? '' : 'hidden'}"><label>${t('lobby.aiDifficulty')}</label><select class="form-control" id="slot-edit-difficulty">${Object.entries(
            DIFFICULTY_LABELS
        )
            .map(([k]) => `<option value="${k}"${slot.aiDifficulty === k ? ' selected' : ''}>${getDifficultyLabel(k)}</option>`)
            .join('')}</select></div>` +
        `<div class="flex gap-md mt-lg"><button class="btn btn-ghost flex-1" id="slot-edit-cancel">${t('common.cancel')}</button><button class="btn btn-primary flex-1" id="slot-edit-save">${t('common.save')}</button></div>` +
        `</div></div>`;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
    const modal = document.getElementById('slot-edit-modal');
    const typeSelect = document.getElementById('slot-edit-type');
    const aiOptions = modal.querySelectorAll('.ai-options');

    typeSelect.onchange = () => {
        aiOptions.forEach((el) => el.classList.toggle('hidden', typeSelect.value !== 'ai'));
    };
    document.getElementById('slot-edit-cancel').onclick = () => modal.remove();
    document.getElementById('slot-edit-save').onclick = () => {
        client.configureSlot(slotIndex, {
            type: typeSelect.value,
            colorId: document.getElementById('slot-edit-color').value || null,
            aiDifficulty: document.getElementById('slot-edit-difficulty').value
        });
        modal.remove();
    };
    modal.onclick = (e) => {
        if (e.target === modal) modal.remove();
    };
}

/**
 * Updates the stats panel with current game state
 * @param {Object} client - MultiplayerClient instance
 */
export function updateStatsPanel(client) {
    if (!client.game || !client.game.sphere || !client.game.sphere.fields) return;

    const playerStats = new Map();
    let sphereTotal = 0;
    let neutralCount = 0;
    const totalFields = client.game.sphere.fields.size;

    for (const player of client.game.players.values()) {
        playerStats.set(player.id, {
            id: player.id,
            name: player.name,
            color: player.color,
            isEliminated: player.isEliminated,
            points: player.points || 0,
            stats: player.stats || {},
            fieldCount: 0,
            totalViruses: 0
        });
    }

    for (const field of client.game.sphere.fields.values()) {
        const value = field.value || 0;
        sphereTotal += value;
        if (!field.ownerId) {
            neutralCount++;
        } else if (playerStats.has(field.ownerId)) {
            const stats = playerStats.get(field.ownerId);
            stats.fieldCount++;
            stats.totalViruses += value;
        }
    }

    const myStats = playerStats.get(client.playerId);
    if (myStats) {
        document.getElementById('my-points').textContent = myStats.points;
        document.getElementById('my-fields').textContent = myStats.fieldCount;
        document.getElementById('my-viruses').textContent = myStats.totalViruses;
        document.getElementById('my-explosions').textContent = myStats.stats?.explosionsCaused || 0;
        document.getElementById('my-conquered').textContent = myStats.stats?.fieldsConquered || 0;
    }

    document.getElementById('neutral-fields').textContent = neutralCount;
    document.getElementById('total-fields').textContent = totalFields;

    const allStats = [...playerStats.values()];
    const activePlayers = allStats
        .filter((p) => !p.isEliminated)
        .sort((a, b) => b.points - a.points || b.fieldCount - a.fieldCount);
    const eliminatedPlayers = allStats.filter((p) => p.isEliminated);
    const sortedPlayers = [...activePlayers, ...eliminatedPlayers];

    document.getElementById('player-ranking').innerHTML = sortedPlayers
        .map((p) => {
            const percent = sphereTotal > 0 ? Math.round((p.totalViruses / sphereTotal) * 100) : 0;
            const pos = p.isEliminated ? '-' : activePlayers.indexOf(p) + 1;
            return (
                `<div class="ranking-item ${p.isEliminated ? 'eliminated' : ''} ${p.id === client.playerId ? 'is-me' : ''}">` +
                `<span class="ranking-position">${pos}.</span>` +
                `<span class="ranking-color" style="background: ${p.color?.hex || '#666'}"></span>` +
                `<span class="ranking-name">${escapeHtml(p.name)}</span>` +
                `<span class="ranking-points">${p.points}</span>` +
                `<span class="ranking-fields">${p.fieldCount}</span>` +
                `<span class="ranking-percent">${percent}%</span></div>`
            );
        })
        .join('');
}

/**
 * Updates the in-game UI (turn indicator, player list, placement hint)
 * @param {Object} client - MultiplayerClient instance
 */
export function updateGameUI(client) {
    const state = client.getGameState();
    if (!state) return;

    const turnBox = document.getElementById('current-turn-box');
    const currentName = document.getElementById('current-player-name');

    if (state.currentPlayer) {
        currentName.textContent = state.currentPlayer.name;
        currentName.style.color = state.currentPlayer.color?.hex || '#fff';
        turnBox.className = `turn-indicator${state.isMyTurn ? ' my-turn' : ''}`;
    }

    const placementHint = document.getElementById('placement-hint');
    const myPlayer = state.players.find((p) => p.id === client.playerId);
    const isPlacementPhase = state.round === 1 && myPlayer && myPlayer.fieldCount === 0 && state.isMyTurn;
    placementHint.classList.toggle('hidden', !isPlacementPhase);

    const list = document.getElementById('game-player-list');
    list.innerHTML = state.players
        .map(
            (p) =>
                `<div class="player-item ${p.isActive ? '' : 'eliminated'} ${p.id === state.currentPlayer?.id ? 'current' : ''} ${p.isConnected === false && !p.isAI ? 'disconnected' : ''}">` +
                `<div class="player-color" style="background: ${p.color?.hex || '#888'}"></div>` +
                `<span class="player-name">${escapeHtml(p.name)}</span>${
                    p.isAI ? `<span class="player-badge ai">${t('lobby.aiBadge')}</span>` : ''
                }${
                    p.isConnected === false && !p.isAI ? `<span class="player-badge offline">${t('game.offline')}</span>` : ''
                }<span class="player-stats">${p.fieldCount} / ${p.totalViruses}</span></div>`
        )
        .join('');
}

/**
 * Formats seconds into MM:SS
 * @param {number} seconds
 * @returns {string}
 */
export function formatTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/** Updates the game elapsed time display */
export function updateGameTime() {
    if (!gameStartTime) return;
    document.getElementById('game-time').textContent = formatTime(Math.floor((Date.now() - gameStartTime) / 1000));
}

/** Updates the turn elapsed time display */
export function updateTurnTime() {
    if (!turnStartTime) return;
    document.getElementById('turn-time').textContent = formatTime(Math.floor((Date.now() - turnStartTime) / 1000));
}

/** Starts game and turn timers */
export function startGameTimers() {
    // Erst laufende Timer stoppen: bei einem In-Game-Reconnect wird diese Funktion
    // erneut aufgerufen (onGameStarted + onGameJoined), sonst wird das alte Interval-
    // Handle ueberschrieben und der erste Timer laeuft unerreichbar weiter (Leak).
    stopGameTimers();
    gameStartTime = Date.now();
    turnStartTime = Date.now();
    gameTimeInterval = setInterval(updateGameTime, 1000);
    turnTimeInterval = setInterval(updateTurnTime, 1000);
}

/** Resets the turn timer */
export function resetTurnTime() {
    turnStartTime = Date.now();
    document.getElementById('turn-time').textContent = '00:00';
}

/** Stops game and turn timers */
export function stopGameTimers() {
    if (gameTimeInterval) {
        clearInterval(gameTimeInterval);
        gameTimeInterval = null;
    }
    if (turnTimeInterval) {
        clearInterval(turnTimeInterval);
        turnTimeInterval = null;
    }
}
